import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../../assets/icons/sidebar/sidebar-payment.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import currentTimeZone from '../../../config/timeZone';
import { TransactionEMSPInterFace } from '../../../interface/emsp-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disablePaymentManagementApplied,
  fetchAllPayments,
  fetchListPaymentMethodFilters,
  updatePaymentManagementFilters,
  setSortData,
} from '../../../redux/slices/EMSPCommercialTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { setSidePanelData } from '../../../redux/slices/TableSidePanelSlice';
import { RootState } from '../../../redux/store';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import EmspCdrSidePanel from '../../../shared-components/table-side-panel/EmspCdrSidePanel';
import EmspSessionSidePanel from '../../../shared-components/table-side-panel/EmspSessionSidePanel';
import EndConsumerSidePanel from '../../../shared-components/table-side-panel/EndConsumerSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween, removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import FormatDuration from '../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import paymentsApi from '../../../utils/api/v2/emsp-transaction';

/**
 * Payments Management function Component
 * @returns {React.JSX.Element} The rendered component.
 */
function CommercialTransactionList(): JSX.Element {
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  const [searchParameters, setSearchParameters] = useSearchParams();

  const {
    payments,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state: RootState) => state.emspCommercialTransaction);
  const isInitialMount = React.useRef(true);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(['locationId', 'invoiceId']);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Retrieves date filter parameters including time zone when a valid date range is selected.
   * @returns {object} The date query parameters.
   */
  const getDateParameters = () => {
    return dateRange.startDate && dateRange.endDate
      ? {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          timeZone: currentTimeZone,
        }
      : {};
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Fetches the payment details and dispatches to Redux store.
   * @param {any} data - The query parameters payload for fetching payments.
   * @param {boolean} [force] - Whether to force the API call regardless of current loading state.
   */
  const fetchPayments = (data: any, force = false) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading || force) {
      dispatch(fetchAllPayments(data));
    }
  };

  /**
   * Function to refresh data.
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const dateParameters = getDateParameters();
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({
      ...queryParameters,
      ...dateParameters,
      page,
      limit,
      ...sortValue,
    });
  };

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: payments,
    disableEdit: true, // ← hides the Edit (pencil) button
    disableDelete: true, // ← hides the Delete (trash) button
  });

  const column = [
    {
      label: t('Created'),
      accessorKey: 'createdAt' as keyof TransactionEMSPInterFace,
      /**
       * Render creation timestamp in formatted string.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The creation timestamp element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{ConvertUTCtoDateTime(rowData.createdAt)}</span>
      ),
    },
    {
      label: t('Last Updated'),
      accessorKey: 'updatedAt' as keyof TransactionEMSPInterFace,
      /**
       * Render updated timestamp in formatted string.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The updated timestamp element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{ConvertUTCtoDateTime(rowData.updatedAt)}</span>
      ),
    },
    {
      label: t('CDRId'),
      accessorKey: 'externalId' as keyof TransactionEMSPInterFace,
      queryParamsKey: 'cdrObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: (
        <EmspCdrSidePanel fetchIdFrom="queryParams" onActionSuccess={handleRefresh} />
      ),
    },
    {
      label: t('sessionId', { defaultValue: 'Session ID' }),
      accessorKey: 'sessionId' as any,
      queryParamsKey: 'sessionId',
      queryParamsValue: 'sessionId',
      sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('driverName'),
      accessorKey: 'userName' as keyof TransactionEMSPInterFace,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
      /**
       * Determines whether the side panel should be disabled for the row.
       * @param {TransactionEMSPInterFace} rowData - The current row data.
       * @returns {boolean} True if side panel is disabled.
       */
      isSidePanelDisabled: (rowData: TransactionEMSPInterFace) =>
        !(rowData.driver?.userCustomId || rowData.userCustomId)?.startsWith('ENR'),
      /**
       * Renders the driver's user ID with the driver's name stacked below it.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} Stacked user ID and driver name element.
       */
      customData: (rowData: TransactionEMSPInterFace): React.JSX.Element => {
        const driverName = rowData.driver
          ? `${rowData.driver.firstName || ''} ${rowData.driver.lastName || ''}`.trim()
          : rowData.driverName || rowData.userName;

        const userId = rowData.driver?.userCustomId || rowData.userCustomId;
        const isEligibleForSidePanel = Boolean(userId && userId.startsWith('ENR'));

        /**
         * Opens the end consumer side panel for the selected driver ID.
         * @param {React.MouseEvent} event - The click event.
         */
        const handleDriverIdClick = (event: React.MouseEvent) => {
          event.stopPropagation();
          if (!isEligibleForSidePanel || !userId) return;

          const parameters = new URLSearchParams(searchParameters.toString());
          const sidePanelKeys = [
            'accountCustomId',
            'deviceId',
            'deviceHubId',
            'connectorDeviceId',
            'stationId',
            'facilityId',
            'userCustomId',
            'userId',
            'utilityId',
            'transactionObjectId',
            'roleId',
            'sessionId',
            'cdrObjectId',
            'paymentDetailsId',
            'personalChargerId',
            'action',
          ];
          sidePanelKeys.forEach((key) => parameters.delete(key));
          parameters.set('userCustomId', userId);
          setSearchParameters(parameters);
          dispatch(
            setSidePanelData({
              key: 'Transactions',
              value: rowData.id || rowData._id,
            }),
          );
        };

        return (
          <div className="driver-info-column">
            <span className="driver-name" title={driverName || '-'}>
              {driverName || '-'}
            </span>

            {isEligibleForSidePanel ? (
              <span
                className="driver-user-id hyperlink"
                role="presentation"
                onClick={handleDriverIdClick}
                title={userId}>
                {userId}
              </span>
            ) : (
              <span className="driver-user-id" title={userId || '-'}>
                {userId || '-'}
              </span>
            )}
          </div>
        );
      },
    },

    {
      label: t('locationId'),
      accessorKey: 'locationId' as keyof TransactionEMSPInterFace,
      /**
       * Custom rendering of Location ID column.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The rendered location ID.
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        const locationId = rowData.location?.id || rowData.locationId || t('na');
        return <span>{locationId}</span>;
      },
    },
    {
      label: t('partyId'),
      accessorKey: 'partyId' as any,
      sortAllow: false,
      /**
       * Render Party ID.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Party ID element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{rowData.partyId || (rowData as any).cdrDetails?.partyId || t('na')}</span>
      ),
    },

    // {
    //   label: t('location', { defaultValue: 'Location' }),
    //   accessorKey: 'location' as any,
    //   /**
    //    * Custom rendering of Location column.
    //    * @param {TransactionEMSPInterFace} rowData - The row data.
    //    * @returns {React.JSX.Element} The rendered location name.
    //    */
    //   customData: (rowData: TransactionEMSPInterFace) => {
    //     const locationName = rowData.location?.name || rowData.chargingPointSn || t('na');
    //     return <span>{locationName}</span>;
    //   },
    // },
    {
      label: t('energy'),
      accessorKey: 'totalEnergy' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render total energy in kWh.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total energy element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{ToFixedValue(rowData.totalEnergy, 3)} kWh</span>
      ),
    },
    {
      label: t('CDRamount'),
      accessorKey: 'totalCost' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total cost element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.totalCost !== undefined && rowData.totalCost !== null
            ? `${Number(rowData.totalCost).toFixed(2)} ${rowData.currency || 'SAR'}`
            : `0.000 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('platformAmount'),
      accessorKey: 'platformAmount' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total cost element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.platformAmount
            ? `${ToFixedValue(Number(rowData.platformAmount))} ${rowData.billingCurrency || 'SAR'}`
            : `0.00 ${rowData.billingCurrency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('surchargeAmount'),
      accessorKey: 'sessionAmount' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render session amount with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The session amount element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.sessionAmount
            ? `${ToFixedValue(Number(rowData.sessionAmount))} ${rowData.billingCurrency || 'SAR'}`
            : `0.00 ${rowData.billingCurrency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('finalAmount'),
      accessorKey: 'finalAmount' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total cost element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.finalAmount !== undefined && rowData.finalAmount !== null
            ? `${ToFixedValue(rowData.finalAmount)} ${rowData.billingCurrency || 'SAR'}`
            : `0.00 ${rowData.billingCurrency || 'SAR'}`}
        </span>
      ),
    },

    {
      label: t('duration', { defaultValue: 'Time' }),
      accessorKey: 'totalTime' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render duration using FormatDuration helper.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total duration element.
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        return <span>{FormatDuration(rowData.totalTime)}</span>;
      },
    },
    {
      label: t('invoiceId'),
      accessorKey: 'invoiceId' as any,
      sortAllow: false,
      /**
       * Render Invoice ID.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Invoice ID element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{rowData?.invoiceId || t('na')}</span>
      ),
    },
    {
      label: t('paymentStatus', { defaultValue: 'Payment Status' }),
      accessorKey: 'paymentStatus' as keyof TransactionEMSPInterFace,
      sortAllow: false,
      /**
       * Render Payment Status badge.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Payment Status badge element.
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        const rawStatus =
          rowData.paymentStatus ||
          rowData.billingStatus ||
          (rowData.isBilled || rowData.isPaid ? 'SUCCESS' : 'PENDING');

        const upperStatus = String(rawStatus || '').toUpperCase();

        let statusClass = 'pending';
        let statusLabel = upperStatus;

        if (upperStatus === 'SUCCESS' || upperStatus === 'PAID') {
          statusClass = 'success';
          statusLabel = t('paid', { defaultValue: 'Paid' });
        } else if (upperStatus === 'FAILED' || upperStatus === 'UNPAID') {
          statusClass = 'failed';
          statusLabel = t('failed', { defaultValue: 'Failed' });
        } else if (upperStatus === 'NOT_APPLICABLE' || upperStatus === 'NOT APPLICABLE') {
          statusClass = 'notApplicable';
          statusLabel = t('notApplicable', { defaultValue: 'Not Applicable' });
        } else {
          statusClass = 'pending';
          statusLabel = t('pending', { defaultValue: 'Pending' });
        }

        return (
          <span className={`billing-status-badge ${statusClass}`}>
            <span className="dot" />
            {statusLabel}
          </span>
        );
      },
    },
  ];

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('CDRId'), accessorKey: 'externalId' },
    { label: t('driverId'), accessorKey: 'userName' },
    { label: t('driverName'), accessorKey: 'driverName' }, 
    { label: t('sessionId', { defaultValue: 'Session ID' }), accessorKey: 'sessionId' },
    { label: t('locationId'), accessorKey: 'locationId' },
    { label: t('partyId', { defaultValue: 'Party ID' }), accessorKey: 'partyId' },
    { label: t('energy'), accessorKey: 'totalEnergy' },
    { label: t('CDRamount'), accessorKey: 'totalCost' },
    { label: t('platformAmount'), accessorKey: 'platformAmount' },
    { label: t('surchargeAmount'), accessorKey: 'sessionAmount' },
    { label: t('finalAmount'), accessorKey: 'finalAmount' },
    { label: t('duration', { defaultValue: 'Time' }), accessorKey: 'totalTime' },
    { label: t('invoiceId', { defaultValue: 'Invoice ID' }), accessorKey: 'invoiceId' },
    {
      label: t('paymentStatus', { defaultValue: 'Payment Status' }),
      accessorKey: 'paymentStatus',
    },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Table pagination function.
   * @param {any} value - The pagination limit value.
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const dateParameters = getDateParameters();
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({
      ...value,
      ...queryParameters,
      ...dateParameters,
      ...sortValue,
    });
  };

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function.
   * @param {string} type - The export format type ('csv' or 'pdf').
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const dateParameters = getDateParameters();
    const payload: any = {
      ...queryParameters,
      ...dateParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    const apiCall = paymentsApi.fetchCommercialTransactionExport(removeEmptyKeys(payload));
    apiCall
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: TransactionEMSPInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'externalId':
                return row.transactionId || row.externalId || t('na');
                  
              case 'userName':
              case 'userCustomId':
                return row.driver?.userCustomId || row.userCustomId || row.userName || t('na');

              case 'driverName':
                return row.driver
                  ? `${row.driver.firstName || ''} ${row.driver.lastName || ''}`.trim()
                  : row.driverName || row.userName || t('na');

              case 'locationId':
                return row.location?.id || row.locationId || t('na');

              case 'totalEnergy':
                return row.totalEnergy !== undefined && row.totalEnergy !== null
                  ? `${ToFixedValue(row.totalEnergy, 3)} kWh`
                  : t('na');

              case 'totalCost':
                return row.totalCost !== undefined && row.totalCost !== null
                  ? `${ToFixedValue(row.totalCost)} ${row.currency || 'SAR'}`
                  : t('na');

              case 'totalTime':
                return FormatDuration(row.totalTime);

              case 'platform':
              case 'platformAmount':
                return row.platformAmount !== undefined && row.platformAmount !== null
                  ? `${ToFixedValue(row.platformAmount)} ${row.billingCurrency || 'SAR'}`
                  : `0.00 ${row.billingCurrency || 'SAR'}`;

              case 'finalAmount':
                return row.finalAmount !== undefined && row.finalAmount !== null
                  ? `${ToFixedValue(row.finalAmount)} ${row.billingCurrency || 'SAR'}`
                  : `0.00 ${row.billingCurrency || 'SAR'}`;

              case 'sessionId':
                return row.sessionId || (row as any).cdrDetails?.sessionId || t('na');

              case 'partyId':
                return row.partyId || (row as any).cdrDetails?.partyId || t('na');

              case 'billingStatus':
              case 'paymentStatus':
                return (
                  (row as any).paymentStatus ||
                  row.billingStatus ||
                  (row as any).payment?.billingStatus ||
                  t('na')
                );

              case 'invoiceId':
              case 'invoiceNumber':
                return row?.invoiceId || (row as any).invoiceNumber || t('na');
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt);
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt);

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'payments-list.csv');
          } else {
            exportPDF(headers, rows, 'payments-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handles table column sorting change.
   * @param {any} sortData - The sort data object containing sortField and sortBy.
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const dateParameters = getDateParameters();
    dispatch(setSortData(sortData));
    fetchPayments({
      ...queryParameters,
      ...dateParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const dateParameters = getDateParameters();
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchPayments(
        {
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
          ...dateParameters,
          ...sortValue,
        },
        true,
      );
      return;
    }

    fetchPayments(
      {
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        ...dateParameters,
        ...sortValue,
      },
      true,
    );

    if (isFilterUpdated) {
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate]);
  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('transactions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  useEffect(() => {
    /**
     * Handles payment action success event to refresh transaction list.
     */
    const handlePaymentActionSuccess = () => {
      handleRefresh();
    };

    window.addEventListener('emsp-payment-action-success', handlePaymentActionSuccess);
    return () => {
      window.removeEventListener('emsp-payment-action-success', handlePaymentActionSuccess);
    };
  }, [filters, dateRange.startDate, dateRange.endDate, page, limit, sortField, sortBy]);

  return (
    <div className="payment-management-wrap">
      <div className="table-content">
        <CustomTable
          dataKey="Transactions"
          version="v2"
          header={column}
          data={payments}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          stickySerialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePaymentManagementFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListPaymentMethodFilters}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={
            permissionCheck('mobilityTransactionsExport') ? exportDataPdfOrCSV : undefined
          }
          hidedColumnNames={['locationId', 'invoiceId']}
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
          height={280}
        />
      </div>
    </div>
  );
}

export default CommercialTransactionList;
